import _UnleashClient from 'unleash-client/lib/client';
import _DefaultStrategy from 'unleash-client/lib/strategy/default-strategy';
import _FlexibleRolloutStrategy from 'unleash-client/lib/strategy/flexible-rollout-strategy';
import _GradualRolloutRandom from 'unleash-client/lib/strategy/gradual-rollout-random';
import _GradualRolloutSessionId from 'unleash-client/lib/strategy/gradual-rollout-session-id';
import _GradualRolloutUserId from 'unleash-client/lib/strategy/gradual-rollout-user-id';
import _UserWithIdStrategy from 'unleash-client/lib/strategy/user-with-id-strategy';

// Vite 8 (Rolldown) double-wraps CJS default exports: import X from 'cjs' → X = { default: Class }
// biome-ignore lint/suspicious/noExplicitAny: CJS interop unwrap
const unwrap = (m: any) => (typeof m === 'function' ? m : (m?.default ?? m));
const UnleashClient = unwrap(_UnleashClient);
const DefaultStrategy = unwrap(_DefaultStrategy);
const FlexibleRolloutStrategy = unwrap(_FlexibleRolloutStrategy);
const GradualRolloutRandom = unwrap(_GradualRolloutRandom);
const GradualRolloutSessionId = unwrap(_GradualRolloutSessionId);
const GradualRolloutUserId = unwrap(_GradualRolloutUserId);
const UserWithIdStrategy = unwrap(_UserWithIdStrategy);

import type { FlagSnapshot } from '../../store/types';
import type { FeatureFlagAdapter, FeatureFlagLogger } from '../types';
import { WaipuUnleashRepository } from './repository';
import { UnleashTogglesSchema } from './unleashSchema';

export function createUnleashAdapter(scriptId: string, logger?: FeatureFlagLogger): FeatureFlagAdapter {
  let json: unknown;
  try {
    json = JSON.parse(document.getElementById(scriptId)?.textContent ?? '[]');
  } catch (error) {
    logger?.warn(`[unleash]: error parsing feature definitions: ${error}`);
    json = [];
  }

  const parsedFlagDefinitions = UnleashTogglesSchema.safeParse(json);

  const definitions = parsedFlagDefinitions.success ? parsedFlagDefinitions.data : [];
  const repo = new WaipuUnleashRepository(definitions);
  // Strategies are registered explicitly because the default resolver uses
  // hostname lookup, which is unavailable in the browser.
  const client = new UnleashClient(repo, [
    new DefaultStrategy(),
    new FlexibleRolloutStrategy(),
    new GradualRolloutRandom(),
    new GradualRolloutUserId(),
    new GradualRolloutSessionId(),
    new UserWithIdStrategy(),
  ]);
  if (logger) {
    client.on('error', (msg: string) => logger.error(msg));
    client.on('warn', (msg: string) => logger.warn(msg));
  }

  return {
    getFlags(context = {}) {
      const snapshot: FlagSnapshot = {};
      for (const toggle of repo.getToggles()) {
        const variant = client.getVariant(toggle.name, context);
        if (!client.isEnabled(toggle.name, context, () => false)) {
          snapshot[toggle.name] = false;
        } else if (variant.enabled && variant.name !== 'disabled') {
          snapshot[toggle.name] = variant.name;
        } else {
          snapshot[toggle.name] = true;
        }
      }

      return snapshot;
    },
    subscribe(onChange) {
      client.on('update', onChange);
      return () => client.off('update', onChange);
    },
    destroy() {
      client.removeAllListeners();
    },
  };
}
